import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import {
  useTerminateSessionMutation,
  useLazyGetActiveConnectionsQuery,
} from '../../redux/apis/GuacamoleManagment/GuacamoleApiSlice';

const RDPViewer = () => {
  const { connectionId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('guac_token');
  const dataSource = localStorage.getItem('guac_dataSource') || 'postgresql';

  const [terminateSession] = useTerminateSessionMutation();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);

  const [fetchActiveSessions] = useLazyGetActiveConnectionsQuery();
  const iframeRef = useRef(null);

  const iframeUrl = `${process.env.REACT_APP_GUAC_BASE_URL }/#/client/${connectionId}?token=${token}`;

  useEffect(() => {
    if (!iframeLoaded) return;

    const timer = setTimeout(() => {
      fetchActiveSessions({ dataSource })
        .unwrap()
        .then((data) => {
          const sessionKey = Object.keys(data || {}).find(
            (key) => data[key].connectionIdentifier === connectionId
          );
          if (sessionKey) {
            setActiveSessionId(sessionKey);
          } else {
            console.warn('⚠️ No active session found for connection', connectionId);
          }
        })
        .catch((err) => console.error('Failed to fetch active sessions', err));
    }, 3000); 

    return () => clearTimeout(timer);
  }, [iframeLoaded, connectionId, dataSource, fetchActiveSessions]);

  const handleTerminate = () => {
    if (!activeSessionId) {
      toast.error('No active session found for this connection');
      return;
    }

    Modal.confirm({
      title: 'Terminate Connection',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: 'Are you sure you want to terminate this RDP session?',
      okText: 'Terminate',
      okType: 'danger',
      onOk: async () => {
        try {
          await terminateSession({
            dataSource,
            connectionIdentifier: activeSessionId,
          }).unwrap();
          toast.success('Session terminated');
          navigate('/rdp-servers');
        } catch (err) {
          toast.error('Failed to terminate session');
          console.error(err);
        }
      },
    });
  };

  return (
    <div className="container-fluid p-0" style={{ height: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center p-3 bg-light border-bottom">
        <div>
          <button
            className="btn btn-outline-secondary btn-sm me-2"
            onClick={() => navigate('/rdp-servers')}
          >
            <i className="bi bi-arrow-left me-1"></i> Back
          </button>
          <button
            className="btn btn-outline-danger btn-sm"
            onClick={handleTerminate}
            disabled={!activeSessionId}
          >
            <i className="bi bi-x-circle me-1"></i> Terminate
          </button>
        </div>
        <span className="text-muted small">
          {iframeLoaded
            ? activeSessionId
              ? 'Connected'
              : 'Waiting for session...'
            : 'Connecting...'}
        </span>
      </div>

      {/* ponytail: Guacamole syncs the clipboard via the async Clipboard API, which a
          cross-origin iframe cannot use unless the parent delegates the permission. */}
      <iframe
        title="RDP Session"
        ref={iframeRef}
        src={iframeUrl}
        allow="clipboard-read; clipboard-write"
        onLoad={() => {
          setIframeLoaded(true);
          // ponytail: Guacamole listens for keys on its own document, so the frame has
          // to hold focus or nothing types. Focus on load and whenever the mouse is over it.
          iframeRef.current?.focus();
        }}
        onMouseEnter={() => iframeRef.current?.focus()}
        style={{
          width: '100%',
          height: 'calc(100vh - 56px)',
          border: 'none',
          backgroundColor: '#000',
        }}
      />
    </div>
  );
};

export default RDPViewer;
